import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from '../../services/firebaseImports';
import { db } from '../../config/firebase';
import { Navigate } from 'react-router-dom';
import { MigrationDataAccessLayer } from '../../services/migrationDataAccessLayer';
import Logger from '../../services/logger';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isLoading: authInitializing } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Initialize migration data access layer
  const migrationDataAccess = new MigrationDataAccessLayer();

  useEffect(() => {
    /** Until Firebase `onAuthStateChanged` finishes, `user` is still null — do not treat as logged out. */
    if (authInitializing) {
      return;
    }

    const checkAdmin = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        // Use dual-mode access to get user profile
        const result = await migrationDataAccess.getUserProfile(user.uid);
        if (result.success && result.data) {
          const userData = result.data;
          
          if (userData?.isAdmin || userData?.isSuperAdmin) {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        Logger.error('Error checking admin status:', error instanceof Error ? error : new Error(String(error)), { error });
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    void checkAdmin();
  }, [user, authInitializing]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute; 